import {
  ScrollableCurriculumTabStrip,
  type ScrollableCurriculumTabStripHandle,
} from "@/components/ui/scrollable-curriculum-tab-strip";
import { Tabs, TabsContent, TabsTrigger } from "@/components/ui/tabs";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  CurriculumParentStripNavSyncContext,
  useCurriculumParentStripNavSync,
} from "@/contexts/CurriculumParentStripNavSyncContext";
import { scheduleScrollHorizontalStripToActiveTab } from "@/lib/scroll-curriculum-tab-strip";
import { cn } from "@/lib/utils";
import { useCallback, useRef } from "react";
import { BeltRank } from "../../MembersArea";

const DEFAULT_TAB_SURFACE = "!bg-gray-200 !text-gray-900";

interface ChulGiProps {
  selectedBelt: BeltRank;
  getBeltColor: (belt: BeltRank) => string;
  hasDemo?: (aliases: string[]) => boolean;
  onWatchDemo?: (aliases: string[]) => void;
  presentation?: "legacy" | "curriculum";
  beltTabSurfaceClass?: string;
}

interface PatternInfo {
  id: string;
  title: string;
  number: number;
  minRank: BeltRank;
  description: string;
  meaning: string;
  movements: string;
  keyTechniques: string[];
  trainingFocus: string[];
}

const patterns: PatternInfo[] = [
  {
    id: "horse",
    title: "ChulGi 1 (Iron Horse)",
    number: 1,
    minRank: "BLACK/WHITE",
    description:
      "ChulGi 1 (Iron Horse) is the foundational pattern in the ChulGi series, representing strength, stability, and unwavering determination.",
    meaning:
      "ChulGi 1 translates to 'Iron Will' and embodies the strength and stability of a horse, representing the foundational qualities needed in advanced martial arts training.",
    movements:
      "The pattern consists of 45 movements and follows a diagram that emphasizes strong, stable stances and powerful techniques.",
    keyTechniques: [
      "Strong foundational stances",
      "Basic to intermediate hand techniques",
      "Fundamental kicking combinations",
      "Core blocking sequences",
      "Power generation techniques",
      "Stable transitional movements",
    ],
    trainingFocus: [
      "Stance stability and power",
      "Basic technique refinement",
      "Power development",
      "Balance and coordination",
      "Foundation strengthening",
      "Mental fortitude",
    ],
  },
  {
    id: "tiger",
    title: "ChulGi 2 (Iron Horse)",
    number: 2,
    minRank: "BLACK/D1",
    description:
      "ChulGi 2 (Iron Horse) builds upon the foundation of ChulGi 1 (Iron Horse), embodying the strength, agility, and fierce determination.",
    meaning:
      "ChulGi 2 represents the strengthening of one's resolve and the deepening of martial arts principles. It symbolizes the transformation of basic techniques into advanced applications through persistent practice and dedication.",
    movements:
      "The pattern consists of 48 movements and follows an advanced diagram that emphasizes the progression from basic to complex techniques. The movements flow in a pattern that represents the strengthening of one's foundation while building towards mastery.",
    keyTechniques: [
      "Intermediate to advanced stance combinations",
      "Complex hand technique sequences",
      "Advanced kicking combinations",
      "Transitional blocking patterns",
      "Dynamic balance techniques",
      "Advanced footwork patterns",
    ],
    trainingFocus: [
      "Technical precision and refinement",
      "Speed and power integration",
      "Advanced balance development",
      "Flow and rhythm mastery",
      "Intermediate breathing techniques",
      "Mental focus and concentration",
    ],
  },
  {
    id: "dragon",
    title: "ChulGi 3 (Iron Horse)",
    number: 3,
    minRank: "BLACK/D2",
    description:
      "ChulGi 3 (Iron Horse) represents the highest level of the ChulGi series, embodying the wisdom, power, and spiritual elevation symbolized by the dragon in martial arts tradition.",
    meaning:
      "ChulGi 3 represents the highest level of determination and mental fortitude in martial arts training, symbolized by the mystical and powerful dragon.",
    movements:
      "The pattern consists of 54 movements and follows a complex diagram that emphasizes advanced techniques and combinations, representing the fluid and powerful movements of the dragon.",
    keyTechniques: [
      "Advanced stance transitions with complex footwork",
      "Master-level hand techniques and combinations",
      "High-level kicking sequences",
      "Advanced blocking patterns",
      "Complex spinning techniques",
    ],
    trainingFocus: [
      "Advanced technique mastery",
      "Power and speed development",
      "Balance and coordination refinement",
      "Mental focus and concentration",
      "Advanced breathing control",
    ],
  },
];

function chulGiFormTabLabel(pattern: PatternInfo): string {
  const m = pattern.title.match(/\(([^)]+)\)/);
  return `${pattern.number}: ${m?.[1] ?? pattern.title}`;
}

const isPatternVisible = (minRank: BeltRank, currentBelt: BeltRank): boolean => {
  const beltRanks: BeltRank[] = [
    "WHITE",
    "YELLOW",
    "ORANGE",
    "BLUE",
    "GREEN",
    "PURPLE",
    "BROWN",
    "RED",
    "NAVY",
    "BLACK/WHITE",
    "BLACK/D1",
    "BLACK/D2",
    "BLACK/D3",
    "BLACK/D4",
    "BLACK/D5",
  ];

  const minRankIndex = beltRanks.indexOf(minRank);
  const currentBeltIndex = beltRanks.indexOf(currentBelt);

  return currentBeltIndex >= minRankIndex;
};

export default function ChulGi({
  selectedBelt,
  getBeltColor: _getBeltColor,
  hasDemo,
  onWatchDemo,
  presentation = "legacy",
  beltTabSurfaceClass = DEFAULT_TAB_SURFACE,
}: ChulGiProps) {
  const visiblePatterns = patterns.filter((pattern) => isPatternVisible(pattern.minRank, selectedBelt));
  const upstreamParentStripSync = useCurriculumParentStripNavSync();
  const chulGiFormsStripRef = useRef<ScrollableCurriculumTabStripHandle>(null);
  const scrollChulGiFormsStrip = useCallback(() => {
    scheduleScrollHorizontalStripToActiveTab(
      chulGiFormsStripRef.current?.getScrollContainer() ?? null,
    );
  }, []);
  const scrollChulGiInnerStripAncestors = useCallback(() => {
    scrollChulGiFormsStrip();
    upstreamParentStripSync?.();
  }, [scrollChulGiFormsStrip, upstreamParentStripSync]);

  if (visiblePatterns.length === 0) {
    return null;
  }

  const surface = beltTabSurfaceClass;

  const triggerClass = cn(
    "inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-black/10 px-3 py-2 text-xs font-medium md:text-sm",
    surface,
    "data-[state=active]:z-10 data-[state=active]:shadow-md data-[state=active]:ring-2 data-[state=active]:ring-gray-900/40 data-[state=active]:ring-offset-2 data-[state=active]:ring-offset-gray-100",
  );

  if (presentation === "curriculum") {
    const outerDefault = visiblePatterns[0]?.id ?? "horse";
    return (
      <div className="rounded-lg bg-gray-50 p-4 md:p-5">
        <Tabs defaultValue={outerDefault} className="w-full space-y-4">
          <ScrollableCurriculumTabStrip ref={chulGiFormsStripRef} ariaLabelPrefix="ChulGi forms">
            {visiblePatterns.map((pattern) => (
              <TabsTrigger key={pattern.id} value={pattern.id} className={triggerClass}>
                {chulGiFormTabLabel(pattern)}
              </TabsTrigger>
            ))}
          </ScrollableCurriculumTabStrip>

          <CurriculumParentStripNavSyncContext.Provider value={scrollChulGiInnerStripAncestors}>
          {visiblePatterns.map((pattern) => (
            <TabsContent key={pattern.id} value={pattern.id} className="mt-0 focus-visible:outline-none">
              <h2 className="mb-2 text-lg font-semibold text-taekwondo-red md:text-xl">
                {chulGiFormTabLabel(pattern)}
              </h2>
              {pattern.number === 1 && hasDemo?.(["chulgi_1_instruction", "chul_gi_1_instruction"]) && (
                <div className="mb-3">
                  <button
                    type="button"
                    className="inline-flex items-center text-sm font-medium text-taekwondo-blue hover:underline"
                    onClick={() => onWatchDemo?.(["chulgi_1_instruction", "chul_gi_1_instruction"])}
                  >
                    Watch Demonstration Video
                  </button>
                </div>
              )}
              {pattern.number === 3 && hasDemo?.(["chul_gi_3", "chulgi_3"]) && (
                <div className="mb-3">
                  <button
                    type="button"
                    className="inline-flex items-center text-sm font-medium text-taekwondo-blue hover:underline"
                    onClick={() => onWatchDemo?.(["chul_gi_3", "chulgi_3"])}
                  >
                    Watch Demonstration Video
                  </button>
                </div>
              )}
              <Tabs defaultValue="pattern-information" className="w-full space-y-4">
                <ScrollableCurriculumTabStrip ariaLabelPrefix={`ChulGi ${pattern.number}`}>
                  <TabsTrigger value="pattern-information" className={triggerClass}>
                    Pattern Information
                  </TabsTrigger>
                  <TabsTrigger value="meaning" className={triggerClass}>
                    Meaning
                  </TabsTrigger>
                  <TabsTrigger value="movements" className={triggerClass}>
                    Movements
                  </TabsTrigger>
                  <TabsTrigger value="key-techniques" className={triggerClass}>
                    Key Techniques
                  </TabsTrigger>
                  <TabsTrigger value="training-focus" className={triggerClass}>
                    Training Focus
                  </TabsTrigger>
                </ScrollableCurriculumTabStrip>

                <TabsContent value="pattern-information" className="mt-0 focus-visible:outline-none">
                  <div>
                    <h4 className="mb-3 text-base font-medium text-taekwondo-red">Pattern Information</h4>
                    <p className="text-gray-700">
                      {pattern.description} This pattern is required for {pattern.minRank} and above.
                    </p>
                  </div>
                </TabsContent>
                <TabsContent value="meaning" className="mt-0 focus-visible:outline-none">
                  <div>
                    <h4 className="mb-3 text-base font-medium text-taekwondo-red">Meaning</h4>
                    <p className="text-gray-700">{pattern.meaning}</p>
                  </div>
                </TabsContent>
                <TabsContent value="movements" className="mt-0 focus-visible:outline-none">
                  <div>
                    <h4 className="mb-3 text-base font-medium text-taekwondo-red">Movements</h4>
                    <p className="text-gray-700">{pattern.movements}</p>
                  </div>
                </TabsContent>
                <TabsContent value="key-techniques" className="mt-0 focus-visible:outline-none">
                  <div>
                    <h4 className="mb-3 text-base font-medium text-taekwondo-red">Key Techniques</h4>
                    <ul className="list-inside list-disc space-y-1 text-gray-700">
                      {pattern.keyTechniques.map((technique, index) => (
                        <li key={index}>{technique}</li>
                      ))}
                    </ul>
                  </div>
                </TabsContent>
                <TabsContent value="training-focus" className="mt-0 focus-visible:outline-none">
                  <div>
                    <h4 className="mb-3 text-base font-medium text-taekwondo-red">Training Focus</h4>
                    <ul className="list-inside list-disc space-y-1 text-gray-700">
                      {pattern.trainingFocus.map((focus, index) => (
                        <li key={index}>{focus}</li>
                      ))}
                    </ul>
                  </div>
                </TabsContent>
              </Tabs>
            </TabsContent>
          ))}
          </CurriculumParentStripNavSyncContext.Provider>
        </Tabs>
      </div>
    );
  }

  return (
    <Accordion type="single" collapsible className="w-full">
      {visiblePatterns.map((pattern) => (
        <AccordionItem key={pattern.id} value={pattern.id}>
          <AccordionTrigger className="hover:no-underline">
            <div className="flex items-center space-x-2 pl-2">
              <span className="font-semibold">
                {pattern.title} ({pattern.number})
              </span>
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <div className="space-y-4 pt-4">
              <div className="rounded-lg bg-gray-50 p-4">
                {pattern.number === 1 && hasDemo?.(["chulgi_1_instruction", "chul_gi_1_instruction"]) && (
                  <button
                    type="button"
                    className="mb-3 inline-flex items-center text-sm font-medium text-taekwondo-blue hover:underline"
                    onClick={() => onWatchDemo?.(["chulgi_1_instruction", "chul_gi_1_instruction"])}
                  >
                    Watch Demonstration Video
                  </button>
                )}
                {pattern.number === 3 && hasDemo?.(["chul_gi_3", "chulgi_3"]) && (
                  <button
                    type="button"
                    className="mb-3 inline-flex items-center text-sm font-medium text-taekwondo-blue hover:underline"
                    onClick={() => onWatchDemo?.(["chul_gi_3", "chulgi_3"])}
                  >
                    Watch Demonstration Video
                  </button>
                )}
                <h4 className="mb-3 text-base font-medium text-taekwondo-red">Pattern Information</h4>
                <p className="mb-4 text-gray-700">
                  {pattern.description} This pattern is required for {pattern.minRank} and above.
                </p>
                <div className="space-y-3">
                  <div>
                    <h4 className="mb-3 text-base font-medium text-taekwondo-red">Meaning</h4>
                    <p className="text-gray-700">{pattern.meaning}</p>
                  </div>
                  <div>
                    <h4 className="mb-3 text-base font-medium text-taekwondo-red">Movements</h4>
                    <p className="text-gray-700">{pattern.movements}</p>
                  </div>
                  <div>
                    <h4 className="mb-3 text-base font-medium text-taekwondo-red">Key Techniques</h4>
                    <ul className="list-inside list-disc space-y-1 text-gray-700">
                      {pattern.keyTechniques.map((technique, index) => (
                        <li key={index}>{technique}</li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h4 className="mb-3 text-base font-medium text-taekwondo-red">Training Focus</h4>
                    <ul className="list-inside list-disc space-y-1 text-gray-700">
                      {pattern.trainingFocus.map((focus, index) => (
                        <li key={index}>{focus}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
}
